import React from "react";

/**
 * Global Error Boundary — catches unhandled React errors (including
 * failed lazy-loaded chunks) and shows a friendly retry UI instead
 * of a white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log for debugging but don't crash
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    // Force a full page reload to fetch fresh assets
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isChunkError =
        this.state.error?.message?.includes("Loading chunk") ||
        this.state.error?.message?.includes("Failed to fetch") ||
        this.state.error?.name === "ChunkLoadError";

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
          <div className="mb-6">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-[#730000] mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {isChunkError
              ? "New version available"
              : "Something went wrong"}
          </h2>
          <p className="text-gray-500 mb-6 max-w-md">
            {isChunkError
              ? "We've released an update. Please reload the page to get the latest version."
              : "An unexpected error occurred. Please try reloading the page."}
          </p>
          <button
            onClick={this.handleRetry}
            className="px-6 py-2.5 bg-[#730000] text-white rounded-lg font-medium hover:bg-[#5a0000] transition-colors"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
